import { insurerIdFromLegacy } from './patient';
import { localeDescriptor } from '../i18n';
import INSURER_NAMES from '../constants/insurerNames.json';

// Insurance display helpers — the one place «why is the share this number?» is
// turned into a sentence, because two surfaces need the same answer: the settle
// drawer's ⓘ and the booking quote.
//
// Insurance is a PARTY to the transaction, not a discount. Never label the
// insurer's share «تخفیف بیمه» — that is the conceptual error the whole feature
// exists to correct (docs/CLINIC_INSURANCE_DESIGN.md §11.4).
//
// Money here is WHOLE RIALS, like everything on the wire; convert at the display
// edge with fmtToman (lib/money.js). A rial figure under a toman label reads as
// ten times the money.

/** WHY a line's insurance share is what it is. Mirrors the wire enum. */
export const SOURCE = {
  NONE: 'NONE',
  COMPUTED: 'COMPUTED',
  MANUAL: 'MANUAL',
  UNAVAILABLE: 'UNAVAILABLE',
  // Three ANSWERS, each with a different thing for the desk to do: register the
  // letter, hand the patient a reimbursement document, or stop asking.
  NEEDS_REFERRAL: 'NEEDS_REFERRAL',
  INDIRECT: 'INDIRECT',
  NOT_COVERED: 'NOT_COVERED',
};

/** How the service's own definition treats insurance (§4.6). */
export const COVERAGE = {
  NOT_COVERED: 'NOT_COVERED',
  DIRECT: 'DIRECT',
  INDIRECT: 'INDIRECT',
};

export const CALC_MODE = {
  CONTRACT: 'CONTRACT',
  FIXED_PATIENT_PCT: 'FIXED_PATIENT_PCT',
  FIXED_INSURER_AMOUNT: 'FIXED_INSURER_AMOUNT',
};

export const INDIRECT_DOC = {
  NONE: 'NONE',
  RECEIPT: 'RECEIPT',
  CLINIC_LETTER: 'CLINIC_LETTER',
};

export const MULTI_RULE = {
  CHAIN: 'CHAIN',
  BASIC_ONLY: 'BASIC_ONLY',
  SUPPLEMENTARY_ONLY: 'SUPPLEMENTARY_ONLY',
  FIRST_COVERING: 'FIRST_COVERING',
};

/**
 * The configuration a service is governed by when it has none of its own.
 *
 * Identical to what the BFF and the service both fall back to, so the drawer
 * opens on the numbers that would actually be used rather than on blanks.
 */
export const DEFAULT_SERVICE_INSURANCE = {
  coverageMode: COVERAGE.DIRECT,
  acceptedBasicInsurerIds: [],
  allowSupplementary: true,
  acceptedSupplementaryInsurerIds: [],
  calcMode: CALC_MODE.CONTRACT,
  patientSharePct: null,
  insurerAmountRial: null,
  insurerCeilingRial: 0,
  maxQuantityPerVisit: 0,
  minDaysBetween: 0,
  requiresDoctorOrder: false,
  conditionsFa: '',
  referralRequiredDirect: false,
  indirectDocument: INDIRECT_DOC.RECEIPT,
  multiInsurerRule: MULTI_RULE.CHAIN,
};

/** The self-pay sentinel — a real, selectable insurer meaning "no cover". */
export const SELF_PAY_INSURER_ID = 'azad';

export const isBasic = (insurer) => (insurer?.kind || 'BASIC') === 'BASIC';
export const isSupplementary = (insurer) => insurer?.kind === 'SUPPLEMENTARY';

/**
 * The insurers a NEW patient may be registered under at this clinic.
 *
 * The platform catalog is Iran's — تأمین اجتماعی, سلامت, نیروهای مسلح and the
 * Iranian supplementaries — plus «آزاد», the self-pay row. None of the Iranian
 * ones insures anybody at a clinic abroad, so there the choice is self-pay alone
 * (hooks/useClinicRegion.js decides "abroad"). The catalog is not edited: a
 * clinic abroad that ever gets insurers of its own will get them as rows, and
 * this filter is the one place that has to learn about them.
 */
export const registrationInsurers = (insurers, { iran = true } = {}) =>
  (iran ? (insurers || []) : (insurers || []).filter((i) => i.id === SELF_PAY_INSURER_ID));

/**
 * The catalog id a DICTATED insurer means, or '' when it names none this clinic
 * offers (PM-0129).
 *
 * The voice service answers in its own vocabulary — social | health | military |
 * other — which the doctor's kit reads as-is, so it stays. The clinic's insurer is
 * a catalog id, and the two never met: a dictated «تأمین اجتماعی» set
 * `insurer = 'social'`, no card lit up, and the policy write after registration was
 * refused for an insurer id that does not exist — the patient was registered with
 * no insurance, and nobody was told. A model that answers with the Persian name
 * instead of the word it was asked for is read the same way.
 *
 * `offered` (optional) is the list the form shows — registrationInsurers() — so a
 * clinic abroad, which offers self-pay alone, never gets a تأمین card filled in.
 */
const VOICE_INSURER_WORD = {
  social: 'tamin', health: 'salamat', military: 'armed', other: SELF_PAY_INSURER_ID,
  // The catalog's own ids, should the model answer with one.
  tamin: 'tamin', salamat: 'salamat', armed: 'armed', [SELF_PAY_INSURER_ID]: SELF_PAY_INSURER_ID,
};
// Matched on foldInsurerName's spelling: أ/إ/آ → ا, ي → ی, ك → ک.
// i18n-exempt: the words a dictation is matched against, never rendered.
const SPOKEN_INSURER_NAME = [
  [/تامین/, 'tamin'],
  [/سلامت/, 'salamat'],
  [/نیروهای مسلح|ارتش|سپاه/, 'armed'],
  [/ازاد|خصوصی/, SELF_PAY_INSURER_ID],
];

export function insurerFromSpeech(word, offered = null) {
  const said = foldInsurerName(word);
  if (!said) return '';
  const id = VOICE_INSURER_WORD[said]
    || SPOKEN_INSURER_NAME.find(([re]) => re.test(said))?.[1]
    || '';
  if (!id) return '';
  if (offered && offered.length && !offered.some((ins) => ins.id === id)) return '';
  return id;
}

/** Insurers grouped for a select: پایه first, then تکمیلی. */
export function groupInsurers(insurers, t) {
  const list = (insurers || []).filter((i) => i.isActive !== false);
  return [
    { label: t('insurance.groups.basic'), options: list.filter(isBasic) },
    { label: t('insurance.groups.supplementary'), options: list.filter(isSupplementary) },
  ].filter((g) => g.options.length);
}

/** Only insurers the clinic can actually verify get the «استعلام» affordance. */
export const canVerify = (insurer) => !!insurer?.taminCode;

/**
 * The insurance state of ONE line, as the UI names it.
 *
 * `UNAVAILABLE` is deliberately distinct from `NONE`: the first means the
 * patient's policy could not be read and check-in will retry, the second means
 * they genuinely have no cover here and the full price is correct. Showing them
 * the same way would tell the operator a transient outage is a settled fact.
 */
export function lineInsuranceState(line) {
  if (!line) return SOURCE.NONE;
  const src = line.insuranceSource || SOURCE.NONE;
  if (src === SOURCE.MANUAL) return SOURCE.MANUAL;
  if (src === SOURCE.UNAVAILABLE) return SOURCE.UNAVAILABLE;
  if ((line.insuranceShare || 0) > 0) return SOURCE.COMPUTED;
  // Nothing was deducted — but WHY differs, and each answer sends the desk
  // somewhere different. Ordered by what can still be fixed today.
  for (const answer of [SOURCE.INDIRECT, SOURCE.NEEDS_REFERRAL, SOURCE.NOT_COVERED]) {
    if (src === answer) return answer;
    if ((line.insuranceLegs || []).some((leg) => leg.source === answer)) return answer;
  }
  return SOURCE.NONE;
}

/** The legs of a line, in applied order, with the ones that pay nothing kept. */
export const legsOf = (line) =>
  [...(line?.insuranceLegs || [])].sort((a, b) => (a.legIndex || 0) - (b.legIndex || 0));

/** Only the legs a patient is actually owed money by — what the cashier tallies. */
export const payingLegs = (line) => legsOf(line).filter((leg) => (leg.shareRial || 0) > 0);

/** Whether any insurer on this line is waiting on a معرفی‌نامه nobody registered. */
export const needsReferral = (line) =>
  legsOf(line).some((leg) => leg.source === SOURCE.NEEDS_REFERRAL);

/**
 * What the patient may claim back themselves — non-zero only where the clinic
 * does not deduct. They still pay it, so it is never subtracted from anything.
 */
export const indirectEstimateOf = (line) =>
  Number(line?.indirectEstimateRial || 0)
  || legsOf(line).reduce((sum, leg) => sum + (leg.indirectEstimateRial || 0), 0);

/**
 * The ⓘ explanation: which insurer, under what فرانشیز. Returns null when there
 * is nothing to explain — an uninsured line shows no row at all rather than a
 * row saying zero.
 */
export function explainInsurance(line, { insurerName, t }) {
  const lines = explainLegs(line, { insurerName, t });
  return lines.length ? lines.join(' · ') : null;
}

/**
 * The ⓘ, one sentence per insurer.
 *
 * A patient may hold one بیمهٔ پایه and several بیمهٔ تکمیلی, each paying a
 * different part of the line for a different reason, so one sentence cannot say
 * it. A leg that paid NOTHING still gets a line — «طرف قرارداد نیست» and
 * «معرفی‌نامه لازم است» are exactly what the operator needs to read, and they are
 * invisible in the total.
 *
 * Returns [] when there is nothing to explain, so the caller renders no row
 * rather than a row saying zero.
 *
 * `nameOf(insurerId, fallback)` names an insurer in the reader's language (see
 * `useInsurerName`); without it the row's own `insurerName` is printed as-is.
 */
export function explainLegs(line, { insurerName, t, fmtMoney, nameOf } = {}) {
  if (!line || !t) return [];
  const name = nameOf || ((id, fallback) => fallback || id);
  const state = lineInsuranceState(line);

  if (state === SOURCE.MANUAL) {
    return [t('insurance.explain.manual', {
      reason: line.insuranceOverrideReason || t('insurance.explain.noReason'),
    })];
  }
  if (state === SOURCE.UNAVAILABLE) return [t('insurance.explain.unavailable')];
  if (state === SOURCE.NOT_COVERED) return [t('insurance.explain.notCovered')];

  const legs = legsOf(line);
  if (!legs.length) {
    // A line billed before legs existed, or by the legacy desk path: the scalar
    // snapshot is all there is, and it describes one insurer.
    if (state === SOURCE.NONE) return [];
    const parts = [name(line.insurerId, insurerName)];
    if (line.patientSharePct != null && line.patientSharePct > 0) {
      parts.push(t('insurance.explain.franchise', { pct: line.patientSharePct }));
    }
    return [parts.filter(Boolean).join(' · ')];
  }

  return legs.map((leg) => {
    const parts = [name(leg.insurerId, leg.insurerName)];
    if (leg.source === SOURCE.NEEDS_REFERRAL) {
      parts.push(t('insurance.explain.needsReferral'));
      return parts.join(' · ');
    }
    if (!leg.contractId) {
      parts.push(t('insurance.explain.notContracted'));
      return parts.join(' · ');
    }
    if (leg.patientSharePct != null && leg.patientSharePct > 0) {
      parts.push(t('insurance.explain.franchise', { pct: leg.patientSharePct }));
    }
    if (leg.referralLetterNumber) {
      parts.push(t('insurance.explain.letter', { number: leg.referralLetterNumber }));
    }
    if (leg.ceilingAppliedRial > 0) parts.push(t('insurance.explain.ceiling'));
    if (leg.source === SOURCE.INDIRECT) {
      parts.push(t('insurance.explain.indirect'));
      if (fmtMoney && leg.indirectEstimateRial > 0) {
        parts.push(fmtMoney(leg.indirectEstimateRial));
      }
      return parts.join(' · ');
    }
    if (fmtMoney && (leg.shareRial || 0) > 0) parts.push(fmtMoney(leg.shareRial));
    if ((leg.shareRial || 0) === 0) parts.push(t('insurance.explain.paidNothing'));
    return parts.join(' · ');
  });
}

// ── معرفی‌نامه ────────────────────────────────────────────────────────────────

/** Letter states, as the desk filters on them. */
export const LETTER_STATUS = {
  ACTIVE: 'active',
  CONSUMED: 'consumed',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
};

/**
 * What a letter is RIGHT NOW, which is not always what its row says: the server
 * materialises expiry on a sweep, so a letter can be stored `active` and be past
 * its window. Showing the row's word would tell the desk a dead letter will pay.
 *
 * `notYet` is separate from `expired` because it is not a problem — the patient
 * obtained the letter early, and it starts working on its own.
 */
export function letterValidity(letter, atMs = Date.now()) {
  if (!letter) return LETTER_STATUS.CANCELLED;
  if (letter.status && letter.status !== LETTER_STATUS.ACTIVE) return letter.status;
  if (letter.validTo && atMs > letter.validTo) return LETTER_STATUS.EXPIRED;
  if (letter.validFrom && atMs < letter.validFrom) return 'notYet';
  // A capped letter with nothing left cannot authorise anything more.
  if (letter.approvedCeilingRial > 0 && !(letter.remainingRial > 0)) {
    return LETTER_STATUS.CONSUMED;
  }
  return LETTER_STATUS.ACTIVE;
}

/** Whether this letter can still pay for something today. */
export const isLetterUsable = (letter, atMs) =>
  letterValidity(letter, atMs) === LETTER_STATUS.ACTIVE;

/**
 * Refuse a letter that could not authorise anything, in the same words the
 * service would — so the desk is corrected before the round trip, not after.
 *
 * Returns a message, or null when the letter is fine.
 */
export function validateReferralLetter(t, input = {}) {
  if (!input.policyId) return t('insurance.letter.errors.policy');
  if (!String(input.letterNumber || '').trim()) return t('insurance.letter.errors.number');
  if (String(input.letterNumber).trim().length > 64) return t('insurance.letter.errors.numberLong');
  if (!input.validFrom || !input.validTo) return t('insurance.letter.errors.window');
  if (Number(input.validFrom) > Number(input.validTo)) return t('insurance.letter.errors.window');
  if (Number(input.approvedCeilingRial || 0) < 0) return t('insurance.letter.errors.ceiling');
  const pct = input.patientSharePctOverride;
  if (pct != null && pct !== '' && (Number(pct) < 0 || Number(pct) > 100)) {
    return t('insurance.letter.errors.pct');
  }
  // Either a list of services or one item type — a letter cannot be scoped both
  // ways, and the service refuses it.
  if ((input.scopeCatalogIds || []).length && input.scopeItemType) {
    return t('insurance.letter.errors.scope');
  }
  return null;
}

// ── The service definition's own configuration (§4.6) ────────────────────────

/**
 * The configuration as the mutation's INPUT wants it.
 *
 * A read carries `id`, `clinicId`, `effectiveFrom` and the rest of the row's own
 * identity; the input takes only what a human decides. Sending the whole object
 * back is how a GraphQL write fails on a field nobody typed.
 */
export function toServiceInsuranceInput(cfg) {
  const c = { ...DEFAULT_SERVICE_INSURANCE, ...(cfg || {}) };
  return {
    coverageMode: c.coverageMode,
    acceptedBasicInsurerIds: c.acceptedBasicInsurerIds || [],
    allowSupplementary: c.allowSupplementary !== false,
    acceptedSupplementaryInsurerIds: c.acceptedSupplementaryInsurerIds || [],
    calcMode: c.calcMode,
    // Only meaningful to the mode that uses it, and the server refuses a mode
    // without its value — so an unused one is sent as null rather than as 0,
    // which would read as a real «فرانشیز ۰٪».
    patientSharePct: c.calcMode === CALC_MODE.FIXED_PATIENT_PCT && c.patientSharePct != null
      ? Number(c.patientSharePct) : null,
    insurerAmountRial: c.calcMode === CALC_MODE.FIXED_INSURER_AMOUNT && c.insurerAmountRial != null
      ? Number(c.insurerAmountRial) : null,
    insurerCeilingRial: Number(c.insurerCeilingRial || 0),
    maxQuantityPerVisit: Number(c.maxQuantityPerVisit || 0),
    minDaysBetween: Number(c.minDaysBetween || 0),
    requiresDoctorOrder: !!c.requiresDoctorOrder,
    conditionsFa: c.conditionsFa || null,
    referralRequiredDirect: !!c.referralRequiredDirect,
    indirectDocument: c.indirectDocument,
    multiInsurerRule: c.multiInsurerRule,
  };
}

/** Refuse a configuration that could not price a line. Returns a message or null. */
export function validateServiceInsurance(t, cfg = {}) {
  if (!Object.values(COVERAGE).includes(cfg.coverageMode)) {
    return t('insurance.config.errors.coverageMode');
  }
  if (cfg.coverageMode === COVERAGE.NOT_COVERED) return null;
  if (cfg.calcMode === CALC_MODE.FIXED_PATIENT_PCT) {
    const pct = Number(cfg.patientSharePct);
    if (cfg.patientSharePct == null || cfg.patientSharePct === '' || Number.isNaN(pct)) {
      return t('insurance.config.errors.pctRequired');
    }
    if (pct < 0 || pct > 100) return t('insurance.config.errors.pctRange');
  }
  if (cfg.calcMode === CALC_MODE.FIXED_INSURER_AMOUNT) {
    const amount = Number(cfg.insurerAmountRial);
    if (cfg.insurerAmountRial == null || cfg.insurerAmountRial === '' || Number.isNaN(amount)) {
      return t('insurance.config.errors.amountRequired');
    }
    if (amount < 0) return t('insurance.config.errors.amountNegative');
  }
  if (Number(cfg.insurerCeilingRial || 0) < 0) return t('insurance.config.errors.ceiling');
  if (Number(cfg.maxQuantityPerVisit || 0) < 0) return t('insurance.config.errors.quantity');
  if (Number(cfg.minDaysBetween || 0) < 0) return t('insurance.config.errors.days');
  return null;
}

/**
 * What this configuration would do to one line — the drawer's live preview.
 *
 * A DELIBERATE second implementation of the chain in `domain/insurance.py`, kept
 * to one screen's worth: the person defining a service has to see the effect
 * before saving, and a round trip per keystroke is not that. It is an ESTIMATE and
 * says so — the server's answer, which knows the contracts and the letters, is
 * what ever reaches an invoice.
 *
 * `legs` are {insurerId, name, kind, patientSharePct} in applied order.
 */
export function previewSplit(cfg, { legs = [], unitRial = 0, quantity = 1 } = {}) {
  const config = { ...DEFAULT_SERVICE_INSURANCE, ...(cfg || {}) };
  const qty = Math.max(1, Number(quantity) || 1);
  const base = Math.max(0, Math.round(Number(unitRial) || 0) * qty);
  const out = { total: base, insurance: 0, patient: base, indirect: 0, legs: [] };
  if (!base || config.coverageMode === COVERAGE.NOT_COVERED) return out;

  const coveredQty = config.maxQuantityPerVisit > 0
    ? Math.min(qty, config.maxQuantityPerVisit) : qty;
  let remainder = coveredQty >= qty ? base : Math.round((base * coveredQty) / qty);

  for (const leg of legs) {
    const isBasic = leg.kind !== 'SUPPLEMENTARY';
    const allowed = isBasic
      ? config.multiInsurerRule !== MULTI_RULE.SUPPLEMENTARY_ONLY
        && (!config.acceptedBasicInsurerIds.length
          || config.acceptedBasicInsurerIds.includes(leg.insurerId))
      : config.allowSupplementary
        && config.multiInsurerRule !== MULTI_RULE.BASIC_ONLY
        && (!config.acceptedSupplementaryInsurerIds.length
          || config.acceptedSupplementaryInsurerIds.includes(leg.insurerId));
    if (!allowed) {
      out.legs.push({ ...leg, share: 0, reason: 'excluded' });
      continue;
    }
    if (!isBasic && config.referralRequiredDirect) {
      out.legs.push({ ...leg, share: 0, reason: 'needsReferral' });
      continue;
    }

    const pct = config.calcMode === CALC_MODE.FIXED_PATIENT_PCT
      ? Number(config.patientSharePct || 0)
      : Number(leg.patientSharePct || 0);
    let share = config.calcMode === CALC_MODE.FIXED_INSURER_AMOUNT
      ? Math.round(Number(config.insurerAmountRial || 0)) * coveredQty
      : Math.round((remainder * (100 - pct)) / 100);
    if (config.insurerCeilingRial > 0) {
      share = Math.min(share, config.insurerCeilingRial * coveredQty);
    }
    share = Math.max(0, Math.min(share, remainder));

    if (config.coverageMode === COVERAGE.INDIRECT) {
      out.indirect += share;
      out.legs.push({ ...leg, share: 0, estimate: share, reason: 'indirect' });
      remainder -= share;
      continue;
    }
    remainder -= share;
    out.insurance += share;
    out.legs.push({ ...leg, share });
    if (config.multiInsurerRule === MULTI_RULE.FIRST_COVERING && share > 0) break;
  }

  out.patient = base - out.insurance;
  return out;
}

/** «سهم بیمار» for a line, in rials. Never negative. */
export const patientShareOf = (line) =>
  Math.max(0, (line?.totalAmount || 0) - (line?.discount || 0) - (line?.insuranceShare || 0));

/** Sum of the insurer's share across an invoice's lines, in rials. */
export const invoiceInsuranceShare = (invoice) =>
  (invoice?.lineItems || []).reduce((sum, li) => sum + (li.insuranceShare || 0), 0);

/**
 * The booking chip: what the desk tells the patient BEFORE they sit down.
 *
 * `none`         — no cover recorded; they pay in full, correctly.
 * `contracted`   — cover applies here.
 * `uncontracted` — they have an insurer this clinic has no agreement with, so
 *                  they pay full price and claim reimbursement themselves. Amber,
 *                  because it is a surprise worth surfacing early.
 * `unavailable`  — we could not check; the desk should not promise anything.
 */
export function bookingCoverState(quote) {
  if (!quote) return 'none';
  if (quote.source === SOURCE.UNAVAILABLE) return 'unavailable';
  if (!quote.insurerId) return 'none';
  return quote.isContracted ? 'contracted' : 'uncontracted';
}

/** Claim statuses, in the order a claim actually moves through them. */
export const CLAIM_STATUSES = [
  'DRAFT', 'SUBMITTED', 'ACCEPTED', 'PARTIALLY_PAID',
  'PAID', 'REJECTED', 'WRITTEN_OFF', 'CANCELLED',
];

/** Whether a claim can still be sent to the insurer. */
export const isSubmittable = (claim) => claim?.status === 'DRAFT';

/** Whether a claim is finished — nothing further will happen to it. */
export const isTerminal = (claim) =>
  ['PAID', 'WRITTEN_OFF', 'CANCELLED'].includes(claim?.status);

/** کسورات — what the insurer did not pay of what was claimed, in rials. */
export const shortfallOf = (claim) =>
  Math.max(0, (claim?.claimedRial || 0) - (claim?.paidRial || 0));

/**
 * Whether a shortfall still needs a decision. A claim that paid less than it
 * claimed and says nothing about the gap is exactly how a claims ledger turns
 * into a pile of unexplained holes.
 */
export const needsDisposition = (claim) =>
  shortfallOf(claim) > 0
  && ['PARTIALLY_PAID', 'REJECTED'].includes(claim?.status)
  && !claim?.shortfallDisposition;

// ── Naming a patient's insurer for display ───────────────────────────────────

/**
 * An insurer's name in the reader's language.
 *
 * The catalog carries both spellings — «تأمین اجتماعی» / «Social Security» — and
 * every screen used to print `nameFa` whatever the panel's language was, so an
 * English panel showed a row of Persian tiles under an English heading. The rule
 * is the one `readsPersianData` already states for stored names: Persian reads
 * `nameFa`, every other language reads `nameEn`. Either falls back to the other,
 * then to the id, so a row the catalog has only half-named still shows something.
 *
 * Arabic is not a column the catalog has, so an Arabic desk read «Self-pay» and
 * «Social Security» in English under Arabic headings. The catalog's ids are
 * fixed by its seed (accounting `insurers`), so their Arabic names travel with
 * this client as data, the way the country names do (constants/insurerNames.json).
 * An id the file does not know falls back to `nameEn` exactly as before.
 */
export function insurerLabel(insurer, locale) {
  if (!insurer) return '';
  const persian = localeDescriptor(locale).readsPersianData === true;
  const lang = String(locale || '').split('-')[0];
  const own = persian ? insurer.nameFa : (INSURER_NAMES[lang]?.[insurer.id] || insurer.nameEn);
  return own || insurer.nameFa || insurer.nameEn || insurer.id || '';
}

/**
 * The catalog insurer a patient row's stored `insurance` string refers to, or null.
 *
 * `patients.insurance` is a legacy DISPLAY STRING — a column patientmanagement now
 * derives from the patient's policy row, and which four different panels wrote to
 * before the catalog existed. The catalog is the only thing that knows an insurer's
 * name in the reader's language, so a list that prints the stored string directly
 * shows «نیروهای مسلح» on one row and «Military» on the next: the same insurer,
 * spelled in two languages, in one column.
 *
 * Resolution goes catalog-first — id, then nameFa, then nameEn — and falls back to
 * `insurerIdFromLegacy`, which is this client's own record of the four labels it used
 * to write. A value that matches none of those returns NULL rather than a guess: the
 * caller shows «—», because silently naming the wrong insurer misstates what the
 * clinic bills, and that is the whole reason the display string stopped being
 * the model.
 */
export function resolveInsurer(value, insurers) {
  const key = foldInsurerName(value);
  if (!key) return null;
  const list = insurers || [];
  const hit = list.find((i) => foldInsurerName(i.id) === key)
    || list.find((i) => foldInsurerName(i.nameFa) === key)
    || list.find((i) => foldInsurerName(i.nameEn) === key);
  if (hit) return hit;
  const legacyId = insurerIdFromLegacy(value);
  return legacyId ? list.find((i) => i.id === legacyId) || null : null;
}

// Fold a name to something comparable: case, the Arabic/Persian letter pairs that
// look identical and compare differently, the zero-width marks, and every separator.
//
// i18n-exempt: a PARSER. These letters are folded, never rendered.
function foldInsurerName(value) {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[\u200b-\u200f]/g, '')
    .replace(/[أإآ]/g, 'ا')
    .replace(/ي/g, 'ی')
    .replace(/ك/g, 'ک')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}
